import React, { useState, useEffect, useRef } from "react";
import BaseLayout from "./BaseLayout";
import "../App.css";
import { API_BASE } from "../api";

const FAQS = [
  {
    question: "How is leaderboard rank determined?",
    answer:
      "Rank is based on a combination of win percentage and total games played. A perfect record with only a few picks won't outrank an active user with a strong record.",
  },
  {
    question: "Why can't I see an upcoming game?",
    answer:
      "Games are pulled automatically from our sports database every day. If a game isn't showing up yet, it likely hasn't been added to the database yet. Check back closer to game day.",
  },
  {
    question: "How do I reset my password?",
    answer:
      'Use the "Forgot Password" link on the login page and we\'ll email you a reset link. You can also change your password any time from the Settings page.',
  },
];

const CONTACT_REASONS = [
  { icon: "fa-lightbulb-o", text: "Suggest a new sport or feature" },
  { icon: "fa-bug",         text: "Report a bug or broken game" },
  { icon: "fa-question",    text: "Ask a question not covered in the FAQ" },
  { icon: "fa-star",        text: "Share feedback or a compliment" },
];

const SupportPage = () => {
  const [name, setName]           = useState('');
  const [email, setEmail]         = useState('');
  const [message, setMessage]     = useState('');
  const [honeypot, setHoneypot]   = useState('');
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitting, setSubmitting]     = useState(false);
  const loadTime = useRef(Date.now());

  useEffect(() => {
    document.body.classList.add("support-page-bg");
    return () => document.body.classList.remove("support-page-bg");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);

    if (!name.trim() || !email.trim() || !message.trim()) {
      setSubmitStatus('error-empty');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/mail/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:        name.trim(),
          email:       email.trim(),
          message:     message.trim(),
          website:     'FlamePicks',
          website_url: honeypot,
          timestamp:   loadTime.current,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setName('');
        setEmail('');
        setMessage('');
      } else {
        const err = await response.json();
        console.error('Failed to send:', err);
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseLayout>
      <div className="sup-page">

        {/* ── Contact section ── */}
        <div className="sup-card">
          <p className="sup-card-title">Contact Us</p>

          <p className="sup-intro">
            Have something on your mind? We read every message. Here are some
            great reasons to reach out:
          </p>

          <div className="sup-reasons">
            {CONTACT_REASONS.map((r, i) => (
              <div key={i} className="sup-reason-item">
                <i className={`fa ${r.icon} sup-reason-icon`} aria-hidden="true" />
                <span>{r.text}</span>
              </div>
            ))}
          </div>

          {/* Honeypot — hidden from real users */}
          <div className="hp-field" aria-hidden="true">
            <label htmlFor="website_url">Website</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={e => setHoneypot(e.target.value)}
            />
          </div>

          <form className="sup-form" onSubmit={handleSubmit}>
            <div className="sup-form-row">
              <div className="sup-field">
                <label className="sup-label">Your Name</label>
                <input
                  type="text"
                  className="sup-input"
                  placeholder="First and last name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  maxLength={100}
                />
              </div>
              <div className="sup-field">
                <label className="sup-label">Your Email</label>
                <input
                  type="email"
                  className="sup-input"
                  placeholder="We'll send a confirmation here"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="sup-field">
              <label className="sup-label">Message</label>
              <textarea
                className="sup-textarea"
                placeholder="Tell us what's on your mind…"
                rows={5}
                value={message}
                onChange={e => setMessage(e.target.value)}
                maxLength={5000}
              />
            </div>

            {submitStatus === 'success' && (
              <p className="sup-status sup-status--success">
                <i className="fa fa-check-circle" /> Message sent! Check your inbox for a confirmation email.
              </p>
            )}
            {submitStatus === 'error' && (
              <p className="sup-status sup-status--error">
                Something went wrong. Please try again later.
              </p>
            )}
            {submitStatus === 'error-empty' && (
              <p className="sup-status sup-status--error">
                Please fill out all fields before submitting.
              </p>
            )}

            <button type="submit" className="sup-submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send Message'}
            </button>
          </form>
        </div>

        {/* ── FAQ section ── */}
        <div className="sup-card">
          <p className="sup-card-title">Frequently Asked Questions</p>
          <div className="sup-faq-list">
            {FAQS.map((faq, i) => (
              <div key={i} className="sup-faq-item">
                <p className="sup-faq-q">
                  <i className="fa fa-fw fa-angle-right sup-faq-icon" />
                  {faq.question}
                </p>
                <p className="sup-faq-a">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </BaseLayout>
  );
};

export default SupportPage;

